import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { listTasks, listTaskUsers } from "../../firebase/tasks";
import { getStatusBadgeClass } from "../../utils/taskHelpers";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";

const BAR_COLORS = ["#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#6366f1", "#14b8a6", "#f97316", "#ef4444", "#06b6d4"];

export function TeamWorkloadReport() {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      if (entries[0]?.contentRect.width) {
        setChartWidth(Math.floor(entries[0].contentRect.width));
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [loading, chartData.length]);

  useEffect(() => {
    async function fetchData() {
      try {
        const usersList = await listTaskUsers();
        const tasks = await listTasks();

        const getName = (uid) => {
          if (uid === "unassigned") return "Unassigned";
          const u = usersList.find(u => u.uid === uid);
          return u ? u.name : "Unknown User";
        };

        const activeTasks = tasks.filter(t => t.status !== "Closed");
        const workloadMap = new Map();

        activeTasks.forEach(task => {
          const key = task.assigneeUid || "unassigned";
          if (!workloadMap.has(key)) {
            workloadMap.set(key, { open: 0, inProgress: 0, onHold: 0 });
          }
          const counts = workloadMap.get(key);
          if (task.status === "Open") counts.open++;
          else if (task.status === "In Progress") counts.inProgress++;
          else if (task.status === "On Hold") counts.onHold++;
        });

        const formatted = Array.from(workloadMap.entries()).map(([uid, stats]) => ({
          name: getName(uid), total: stats.open + stats.inProgress + stats.onHold,
          open: stats.open, inProgress: stats.inProgress, onHold: stats.onHold
        }));

        formatted.sort((a, b) => b.total - a.total);
        setChartData(formatted);
      } catch (err) {
        setError(err.message || "Failed to load workload data");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  return (
    <div className="container-fluid min-h-screen py-8 px-4 md:px-8" style={{ backgroundColor: "var(--theme-color-canvas)" }}>
      <div
        className="mb-8 pb-5 flex flex-col md:flex-row justify-between items-start md:items-center"
        style={{ borderBottom: "1px solid var(--theme-color-mute)" }}
      >
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight m-0" style={{ color: "var(--theme-color-ink)", fontFamily: "var(--theme-font-family-display)" }}>
            Team Workload Report
          </h1>
          <p className="mt-1 text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>
            Non-closed task distribution across team members.
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <Link
            to="/tasks"
            className="inline-flex items-center justify-center font-semibold py-2 px-4 rounded-lg border transition-colors text-sm no-underline"
            style={{ backgroundColor: "var(--theme-color-canvas-soft)", color: "var(--theme-color-body)", borderColor: "var(--theme-color-mute)" }}
          >
            ← Task Inbox
          </Link>
        </div>
      </div>

      {loading && (
        <div className="animate-pulse flex flex-col space-y-4">
          <div className="h-96 rounded-2xl w-full" style={{ backgroundColor: "var(--theme-color-canvas-soft)" }} />
          <div className="h-64 rounded-2xl w-full" style={{ backgroundColor: "var(--theme-color-canvas-soft)" }} />
        </div>
      )}

      {error && <div className="p-4 rounded-xl text-sm font-medium" style={{ backgroundColor: "#fee2e2", color: "#991b1b" }}>Error: {error}</div>}

      {!loading && !error && chartData.length === 0 && (
        <div className="text-center py-12 rounded-2xl border border-dashed" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}>
          <h3 className="text-lg font-bold mb-2" style={{ color: "var(--theme-color-ink)" }}>No Active Tasks</h3>
          <p className="text-sm" style={{ color: "var(--theme-color-body-mid)" }}>There are currently no open, in progress, or on hold tasks across the team.</p>
        </div>
      )}

      {!loading && !error && chartData.length > 0 && (
        <>
          <div className="rounded-2xl border shadow-sm p-4 sm:p-6 mb-8 overflow-hidden flow-root" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)", borderRadius: "var(--theme-card-radius)" }}>
            <h2 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: "var(--theme-color-body-mid)" }}>Tasks per Team Member</h2>
            <div ref={containerRef} className="w-full relative overflow-hidden" style={{ minHeight: `${Math.max(180, chartData.length * 48)}px` }}>
              {chartWidth > 0 && (
                <BarChart
                  width={chartWidth}
                  height={Math.max(180, chartData.length * 48)}
                  data={chartData}
                  layout="vertical"
                  margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                >
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "var(--theme-color-body)" }} />
                  <YAxis type="category" dataKey="name" width={chartWidth < 500 ? 95 : 120} tick={{ fontSize: chartWidth < 500 ? 11 : 12, fill: "var(--theme-color-ink)" }} />
                  <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid var(--theme-color-mute)", backgroundColor: "var(--theme-card-bg)" }} />
                  <Bar dataKey="total" name="Total Tasks" barSize={chartWidth < 500 ? 22 : 28} radius={[0, 6, 6, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={entry.name} fill={BAR_COLORS[index % BAR_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              )}
            </div>
          </div>

          <div className="hidden lg:block rounded-2xl border shadow-sm overflow-hidden flow-root" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 z-10" style={{ backgroundColor: "var(--theme-color-canvas-soft)", borderBottom: "1px solid var(--theme-color-mute)" }}>
                  <tr className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--theme-color-body-mid)" }}>
                    <th className="p-4 pl-6">Team Member</th>
                    <th className="p-4 w-28">Open</th>
                    <th className="p-4 w-28">In Progress</th>
                    <th className="p-4 w-28">On Hold</th>
                    <th className="p-4 pr-6 w-28">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.map(row => (
                    <tr key={row.name} className="transition-colors" style={{ borderBottom: "1px solid var(--theme-color-canvas-soft)" }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = "var(--theme-color-canvas-soft)")}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = "transparent")}>
                      <td className="p-4 pl-6 font-semibold" style={{ color: "var(--theme-color-ink)" }}>{row.name}</td>
                      <td className="p-4"><span className={getStatusBadgeClass("Open")}>{row.open}</span></td>
                      <td className="p-4"><span className={getStatusBadgeClass("In Progress")}>{row.inProgress}</span></td>
                      <td className="p-4"><span className={getStatusBadgeClass("On Hold")}>{row.onHold}</span></td>
                      <td className="p-4 pr-6 font-extrabold text-lg" style={{ color: "var(--theme-color-ink)" }}>{row.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="lg:hidden space-y-4">
            {chartData.map(row => (
              <div key={row.name} className="rounded-2xl border shadow-sm p-5 space-y-3" style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)", borderRadius: "var(--theme-card-radius)" }}>
                <div className="flex justify-between items-center">
                  <h3 className="text-base font-bold m-0" style={{ color: "var(--theme-color-ink)" }}>{row.name}</h3>
                  <span className="text-xl font-extrabold" style={{ color: "var(--theme-color-primary)" }}>{row.total}</span>
                </div>
                <div className="pt-2 grid grid-cols-3 gap-3 text-center text-xs" style={{ borderTop: "1px solid var(--theme-color-canvas-soft)" }}>
                  <div>
                    <span className="block font-medium mb-1" style={{ color: "var(--theme-color-body-mid)" }}>Open</span>
                    <span className={getStatusBadgeClass("Open")}>{row.open}</span>
                  </div>
                  <div>
                    <span className="block font-medium mb-1" style={{ color: "var(--theme-color-body-mid)" }}>In Progress</span>
                    <span className={getStatusBadgeClass("In Progress")}>{row.inProgress}</span>
                  </div>
                  <div>
                    <span className="block font-medium mb-1" style={{ color: "var(--theme-color-body-mid)" }}>On Hold</span>
                    <span className={getStatusBadgeClass("On Hold")}>{row.onHold}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default TeamWorkloadReport;
